import React, { useMemo, useState } from 'react';
import { AlertBanner, Divider, ExpandableText, InputTextArea, TagGeneral, Text, useReadOnly } from '@spark';
import AdditionalFieldsRail from './AdditionalFieldsRail';
import JsonCodeTextArea from './JsonCodeTextArea';
import OverrideModeControl from './OverrideModeControl';
import PromptTemplateEditor, { PromptTemplateSegments } from './PromptTemplateEditor';
import TemplatePreviewToggle from './TemplatePreviewToggle';
import WhenEvaluatingSection from './WhenEvaluatingSection';
import { getJsonSchemaError } from '../utils/inferJsonSchema';
import { insertTokenIntoTemplate } from '../utils/promptTemplate';
import {
    OVERRIDE_MODES,
    overrideModeFromValue,
    overrideValueForMode,
} from '../utils/specificAIOverrides';

const SECTION_COPY = {
    systemPrompt: {
        title: 'System Prompt',
        same: 'The SpecificAI model uses the commercial system instructions.',
        disable: 'No system instructions are sent.',
        customize: 'These instructions are used only by the SpecificAI model.',
        inheritedLabel: 'Inherited System instructions',
        emptyInherited: 'No system instructions on the commercial model.',
    },
    userPrompt: {
        title: 'User Prompt',
        same: 'The SpecificAI model will use this prompt as-is.',
        disable: 'The SpecificAI model will read only the model input field. No prompt is sent.',
        customize: 'This prompt is used only by the SpecificAI model.',
        inheritedLabel: 'Inherited User prompt',
        emptyInherited: 'No user prompt on the commercial model.',
    },
    schema: {
        title: 'Output format',
        same: 'The SpecificAI model uses the commercial output format.',
        disable: 'No output schema. The model may return free text.',
        customize: 'This schema is used only by the SpecificAI model.',
        inheritedLabel: 'Inherited output format',
        emptyInherited: 'No output format on the commercial model.',
    },
};

const InheritedPreview = ({ label, children }) => (
    <div className="model-setup-inherited-preview">
        <div className="model-setup-inherited-preview__head fs-unmask">
            <Text variant="base-medium" color="primary" as="span">
                {label}
            </Text>
            <TagGeneral label="Inherited" className="model-setup-inherited-preview__tag" />
        </div>
        <div className="model-setup-inherited-preview__body">{children}</div>
    </div>
);

/**
 * SpecificAI model tab (Figma "SpecificAI model · …" frames): per-section
 * tri-state overrides for the student model — Same as commercial / Empty /
 * Customize — plus the shared "When evaluating" cleanup flags.
 */
const SpecificAITab = ({
    role,
    teacherValues,
    overrides,
    onOverridesChange,
    autosaveError,
    clearAutosaveError,
    exampleValue = '',
    paramValues = {},
    extraParamsOptions = {},
    extraParamsLoading = false,
    normalization,
    onNormalizationChange,
    evaluatedSample = null,
    labelNames = null,
}) => {
    const isViewer = role === 'Viewer';
    const { readOnly } = useReadOnly();
    const [userPromptMode, setUserPromptMode] = useState('template');
    const [fieldsRailEnabled, setFieldsRailEnabled] = useState(false);
    // The selected mode is derived from the override value (null / '' / text),
    // but "Customize" with no text yet persists as '' — the same value as
    // "Empty". Remember the user's explicit choice so the editor stays open
    // until they type (or pick another mode).
    const [modeIntents, setModeIntents] = useState({});

    const modes = useMemo(() => {
        const deriveMode = (key) => {
            const derived = overrideModeFromValue(overrides?.[key]);
            if (
                modeIntents[key] === OVERRIDE_MODES.customize &&
                derived === OVERRIDE_MODES.disable
            ) {
                return OVERRIDE_MODES.customize;
            }
            return derived;
        };
        return {
            systemPrompt: deriveMode('systemPrompt'),
            userPrompt: deriveMode('userPrompt'),
            schema: deriveMode('schema'),
        };
    }, [overrides, modeIntents]);

    const setOverride = (key, nextValue) => {
        onOverridesChange({ ...overrides, [key]: nextValue });
    };

    const handleModeChange = (key) => (mode) => {
        setModeIntents((prev) => ({ ...prev, [key]: mode }));
        setOverride(key, overrideValueForMode(mode, {
            teacherValue: teacherValues?.[key] || '',
            currentValue: overrides?.[key],
        }));
    };

    const studentSchemaError = modes.schema === OVERRIDE_MODES.customize
        ? getJsonSchemaError(overrides?.schema)
        : null;

    const renderSectionHead = (key, extra = null) => (
        <>
            <Text variant="lead-semibold" color="primary" as="div" className="fs-unmask">
                {SECTION_COPY[key].title}
            </Text>
            <div className="model-setup-specificai-section__helper-row">
                <Text variant="detail-regular" color="secondary" as="div" className="fs-unmask">
                    {SECTION_COPY[key][modes[key]]}
                </Text>
                {extra}
            </div>
        </>
    );

    return (
        <div className="model-setup-specificai-tab">
            {autosaveError && (
                <AlertBanner
                    feedback="error"
                    showButton={false}
                    text={(
                        <div>
                            <div>Could not save SpecificAI model settings</div>
                            <div>{autosaveError}</div>
                        </div>
                    )}
                    onCloseClick={clearAutosaveError}
                />
            )}

            <section className="model-setup-specificai-section" data-section="system-prompt">
                {renderSectionHead('systemPrompt')}
                <OverrideModeControl
                    mode={modes.systemPrompt}
                    onChange={handleModeChange('systemPrompt')}
                    disabled={readOnly || isViewer}
                    ariaLabel="System prompt override mode"
                />
                {modes.systemPrompt === OVERRIDE_MODES.same && (
                    <InheritedPreview label={SECTION_COPY.systemPrompt.inheritedLabel}>
                        {(teacherValues?.systemPrompt || '').trim() ? (
                            // Rendered exactly like the Commercial tab's System
                            // Prompt textarea (own line breaks kept), clamped to
                            // 3 lines with Show more / Show less.
                            <ExpandableText
                                text={teacherValues.systemPrompt}
                                maxLines={3}
                                preserveWhitespace
                                color="primary"
                            />
                        ) : (
                            <Text variant="base-regular" color="primary" as="div">
                                {SECTION_COPY.systemPrompt.emptyInherited}
                            </Text>
                        )}
                    </InheritedPreview>
                )}
                {modes.systemPrompt === OVERRIDE_MODES.customize && (
                    <InputTextArea
                        showLabel={false}
                        placeholder="e.g. You write one-line ticket summaries. No greetings."
                        value={overrides?.systemPrompt || ''}
                        onChange={(e) => setOverride('systemPrompt', e.target.value)}
                        rows={3}
                        stretch
                        disabled={readOnly || isViewer}
                        className="model-setup-specificai-section__textarea"
                        aria-label="SpecificAI system prompt"
                    />
                )}
            </section>

            <Divider />

            <section className="model-setup-specificai-section" data-section="user-prompt">
                <div className="model-setup-specificai-section__grid">
                    <div className="model-setup-specificai-section__main">
                        {renderSectionHead(
                            'userPrompt',
                            modes.userPrompt === OVERRIDE_MODES.customize ? (
                                <TemplatePreviewToggle mode={userPromptMode} onChange={setUserPromptMode} />
                            ) : null
                        )}
                        <OverrideModeControl
                            mode={modes.userPrompt}
                            onChange={handleModeChange('userPrompt')}
                            disabled={readOnly || isViewer}
                            ariaLabel="User prompt override mode"
                        />
                        {modes.userPrompt === OVERRIDE_MODES.same && (
                            <InheritedPreview label={SECTION_COPY.userPrompt.inheritedLabel}>
                                {(teacherValues?.userPrompt || '').trim() ? (
                                    // Same treatment as the inherited System Prompt:
                                    // no editor box, own line breaks kept, clamped to
                                    // 3 lines with Show more / Show less — with the
                                    // template tokens still rendered as pills.
                                    <ExpandableText maxLines={3} preserveWhitespace color="primary">
                                        <PromptTemplateSegments value={teacherValues.userPrompt} />
                                    </ExpandableText>
                                ) : (
                                    <Text variant="base-regular" color="primary" as="div">
                                        {SECTION_COPY.userPrompt.emptyInherited}
                                    </Text>
                                )}
                            </InheritedPreview>
                        )}
                        {modes.userPrompt === OVERRIDE_MODES.customize && (
                            <PromptTemplateEditor
                                value={overrides?.userPrompt || ''}
                                onChange={(next) => setOverride('userPrompt', next)}
                                mode={userPromptMode}
                                disabled={readOnly || isViewer}
                                placeholder="Write the prompt used only by the SpecificAI model."
                                exampleValue={exampleValue}
                                paramValues={paramValues}
                                ariaLabel="SpecificAI user prompt"
                            />
                        )}
                    </div>
                    {modes.userPrompt === OVERRIDE_MODES.customize && (
                        <div className="model-setup-specificai-section__rail">
                            <AdditionalFieldsRail
                                fields={extraParamsOptions}
                                promptTemplate={overrides?.userPrompt || ''}
                                exampleValue={exampleValue}
                                enabled={fieldsRailEnabled}
                                loading={extraParamsLoading}
                                onEnabledChange={setFieldsRailEnabled}
                                onInsert={(name) =>
                                    setOverride(
                                        'userPrompt',
                                        insertTokenIntoTemplate(overrides?.userPrompt || '', name)
                                    )
                                }
                                disabled={isViewer}
                            />
                        </div>
                    )}
                </div>
            </section>

            <Divider />

            <section className="model-setup-specificai-section" data-section="output-format">
                {renderSectionHead('schema')}
                <OverrideModeControl
                    mode={modes.schema}
                    onChange={handleModeChange('schema')}
                    disabled={readOnly || isViewer}
                    ariaLabel="Output format override mode"
                />
                {modes.schema === OVERRIDE_MODES.same && (
                    <InheritedPreview label={SECTION_COPY.schema.inheritedLabel}>
                        {(teacherValues?.schema || '').trim() ? (
                            <pre className="model-setup-inherited-preview__code">
                                {teacherValues.schema}
                            </pre>
                        ) : (
                            <Text variant="base-regular" color="primary" as="div">
                                {SECTION_COPY.schema.emptyInherited}
                            </Text>
                        )}
                    </InheritedPreview>
                )}
                {modes.schema === OVERRIDE_MODES.customize && (
                    <>
                        <JsonCodeTextArea
                            placeholder={'e.g. {"type": "object", "properties": {"summary": {"type": "string"}}}'}
                            value={overrides?.schema || ''}
                            onChange={(e) => setOverride('schema', e.target.value)}
                            rows={7}
                            disabled={readOnly || isViewer}
                        />
                        {studentSchemaError ? (
                            <div className="model-setup-json-schema__error fs-unmask">
                                <Text variant="detail-regular" as="div">
                                    {studentSchemaError}
                                </Text>
                            </div>
                        ) : null}
                    </>
                )}
            </section>

            <Divider />

            <WhenEvaluatingSection
                normalization={normalization}
                onChange={onNormalizationChange}
                disabled={readOnly || isViewer}
                evaluatedSample={evaluatedSample}
                labelNames={labelNames}
            />
        </div>
    );
};

export default SpecificAITab;
